import { execSync } from 'node:child_process'
import { existsSync } from 'node:fs'
import { resolve } from 'node:path'

export function isGitInstalled(): boolean {
  try {
    execSync('git --version', { stdio: 'ignore' })
    return true
  } catch {
    return false
  }
}

// Pure parser for a `remote.origin.url`, extracted so it can be tested against
// fixed inputs (the live remote varies by environment — a proxied checkout, a
// token-auth HTTPS clone — and must not couple the test to the sandbox).
//
// Routing is SCHEME-FIRST: any `scheme://…` URL (http, https, ssh://) goes through
// the URL parser, which correctly handles userinfo (`user@host`, `user:token@host`)
// — the old `@`-means-SSH heuristic mis-parsed every authenticated HTTPS remote to
// null. Only scp-like `git@host:org/repo` (no scheme) takes the SSH branch.
// A real forge remote is exactly two path segments (org/repo). Claude Code remote
// checkouts may add a local proxy prefix (`/git/org/repo`); in that case take the
// final two path segments. Other nested paths are rejected on purpose.
export function parseGitRemoteUrl(url: string): { org: string; repo: string } | null {
  const clean = url.trim().replace(/\.git$/, '')
  if (!clean) return null
  let org: string | undefined
  let repo: string | undefined

  if (clean.includes('://')) {
    // Scheme-qualified (https://host/org/repo, ssh://git@host/org/repo) — URL()
    // strips any userinfo so org/repo come from the path alone.
    try {
      const parsed = new URL(clean)
      const segments = parsed.pathname.split('/').filter(Boolean)
      if (segments.length === 2) {
        ;[org, repo] = segments
      } else if (segments.length === 3 && segments[0] === 'git') {
        ;[org, repo] = segments.slice(-2)
      } else {
        return null
      }
    } catch {
      return null
    }
  } else if (clean.includes('@')) {
    // scp-like SSH: git@host:org/repo
    const afterAt = clean.split('@')[1]
    if (!afterAt) return null
    const afterColon = afterAt.split(':')[1]
    if (!afterColon) return null
    if (afterColon.split('/').length !== 2) return null
    ;[org, repo] = afterColon.split('/')
  } else {
    return null
  }

  if (!org || !repo || !/^[a-zA-Z0-9_.-]+$/.test(org) || !/^[a-zA-Z0-9_.-]+$/.test(repo)) {
    return null
  }
  return { org, repo }
}

export function getGitRemote(): { org: string; repo: string } | null {
  try {
    const url = execSync('git config --get remote.origin.url', {
      encoding: 'utf8',
      cwd: process.cwd(),
      // Suppress git's own stderr — callers handle the null/empty case with
      // their own messaging; a raw `fatal: ...` must never leak to the user.
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim()
    return parseGitRemoteUrl(url)
  } catch {
    return null
  }
}

export function isGitRepo(): boolean {
  return getRepoRoot() !== null
}

export function getRepoRoot(): string | null {
  try {
    // stderr ignored: outside a repo git prints `fatal: not a git repository`,
    // but isGitRepo()/callers handle the null return with their own messaging.
    return execSync('git rev-parse --show-toplevel', {
      encoding: 'utf8',
      cwd: process.cwd(),
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim()
  } catch {
    return null
  }
}

export function getUncommittedDiff(): string {
  try {
    return execSync('git diff', { encoding: 'utf8', cwd: process.cwd() }).trim()
  } catch {
    return ''
  }
}

export function getStagedDiff(): string {
  try {
    return execSync('git diff --staged', { encoding: 'utf8', cwd: process.cwd() }).trim()
  } catch {
    return ''
  }
}

export function getLastCommitHash(): string {
  try {
    return execSync('git rev-parse HEAD', { encoding: 'utf8', cwd: process.cwd() }).trim()
  } catch {
    return 'unknown'
  }
}

export function getChangedFilesFromDiff(diff?: string): string[] {
  const text = diff ?? getUncommittedDiff()
  if (!text) return []
  const files = new Set<string>()
  for (const line of text.split('\n')) {
    if (line.startsWith('+++ b/')) {
      files.add(line.slice(6))
    } else if (line.startsWith('--- a/')) {
      // skip, we get the path from +++ line
    } else if (line.startsWith('diff --git ')) {
      const match = line.match(/diff --git a\/(.+?) b\/(.+)$/)
      if (match) files.add(match[2])
    }
  }
  return [...files]
}

export function getChangedFilesList(): string[] {
  try {
    // Include both staged and unstaged changes for local context matching.
    const unstaged = execSync('git diff --name-only HEAD', { encoding: 'utf8', cwd: process.cwd() }).trim()
    const staged = execSync('git diff --staged --name-only', { encoding: 'utf8', cwd: process.cwd() }).trim()
    const files = new Set<string>()
    if (unstaged) unstaged.split('\n').forEach(f => files.add(f))
    if (staged) staged.split('\n').forEach(f => files.add(f))
    return [...files].filter(Boolean)
  } catch {
    return []
  }
}

export function getAuthorName(): string {
  try {
    return execSync('git config user.name', { encoding: 'utf8', cwd: process.cwd() }).trim()
  } catch {
    return 'unknown'
  }
}
